import {
  AddIcon,
  Button,
  Chip,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  Grid,
  GridColumn,
  GridRow,
  LockIcon,
  Row,
  showToast,
  Table,
  TableColumns,
  Tooltip,
  UnlockIcon,
  useDefaultSelecting,
  VisibleIcon,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTags } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { LessonTabFilterType } from '../../../pages/Deputate/Plan/Lesson/LessonsPage';
import {
  _UsedInVersionLessonsDocument,
  _UsedInVersionLessonsQuery,
  _UsedInVersionLessonsQueryVariables,
  InheritLessonTeachersType,
  useInheritLessonTeachersMutation,
  useMergeLessonsMutation,
} from '../../../types/planung-graphql-client-defs';
import { partition } from '../../../utils/arrayFunc';
import { showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { onlyUnique } from '../../../utils/unique-filter';
import { urqlClient } from '../../../utils/urqlClient';
import { LessonCard } from '../../LessonCard/LessonCard';
import { UsedInVersionsTooltipContent } from '../../TooltipContents/UsedInVersionTooltipContent/UsedInVersionsTooltipContent';
import { useDeputateLessons } from '../hooks/useDeputateLessons';
import { DeputateEpochModal } from '../modals/DeputateEpochModal';
import { DeputateLessonModal } from '../modals/DeputateLessonModal';
import { MergeSubjectContainerLessonsModal } from '../modals/MergeSubjectContainerLessonsModal/MergeSubjectContainerLessonsModal';
import { LessonTableType } from '../TimetableVersion/Table/TimetableVersionLessonsTable';
import { LessonFormType } from '../types';
import style from './DeputateLessonsTable.module.scss';

type LessonOverviewTableProps = PropsWithChildren & {
  classUuid?: string | undefined;
  teacherUuid?: string | undefined;
  lessonsData: LessonTableType[];
  lessonTabFilterType?: LessonTabFilterType;
  configKey: string;
  defaultInitialValues?: Partial<LessonFormType>;
};

export const DeputateLessonsTable = observer(
  ({
    classUuid,
    lessonsData,
    lessonTabFilterType,
    configKey,
    defaultInitialValues,
    teacherUuid,
  }: LessonOverviewTableProps) => {
    const { t } = useTranslation();
    const { pimAuthClaims } = useAuthClaims();
    const schoolYear = useUserConfigContext().selectedSchoolYear;
    const context = useMemorizedCacheTags(['LESSONS', 'LESSON_CLASS']);
    const permissionChecker = usePermissionChecker();

    const { columnVisibility, saveColumnVisibility } = useHiddenColumns(configKey, {
      'lesson.groups': true,
      classes: false,
      groupNames: false,
    });
    const { sorting, saveSorting } = useColumnsSort(configKey);
    const { rowSelection, onRowSelectionChange, clearSelection } = useDefaultSelecting();

    const [loading, setLoading] = useState(false);
    const [lessonUuid, setLessonUuid] = useState<string | null>(null);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [epochModalOpen, setEpochModalOpen] = useState<boolean>(false);

    const [lessonsToMerge, setLessonsToMerge] = useState<LessonTableType[]>([]);

    const [mergeSubjectContainerLessonsModalOpen, setMergeSubjectContainerLessonsModalOpen] = useState<boolean>(false);

    const { confirm, ConfirmationDialog } = useConfirm();

    const {
      detachLessons,
      assignLessons,
      duplicateLesson,
      deleteLessons,
      updateCheckboxes: update,
      loadingState,
      queryLessonsData,
    } = useDeputateLessons({
      organizationUuid: pimAuthClaims.getOrganizationUuid(),
      schoolYearUuid: schoolYear?.uuid ?? '',
    });

    const [, mergeLessons] = useMergeLessonsMutation();

    const updateCheckboxes = useCallback(
      async ({
        type,
        uuids,
        value,
      }: {
        type: 'teachingLoadEnabled' | 'timetableEnabled' | 'elective';
        uuids: string[];
        value: boolean;
      }) => {
        await update({
          type,
          uuids,
          value,
        });
        onRowSelectionChange({});
      },
      [onRowSelectionChange, update],
    );

    const handleAssign = useCallback(
      async (item: LessonTableType, teachers: { uuid: string }[]) => {
        // allowed only with max 1 curriculum Person, so we take the first anyway
        const curriculumPersonProperties = queryLessonsData?.lessons
          .find((lesson) => lesson.uuid === item.uuid)
          ?.curriculum?.personsConnection.edges.map((edge) => edge.properties)[0];

        await assignLessons([item.uuid], teachers, curriculumPersonProperties);
      },
      [queryLessonsData, assignLessons],
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
    const handleDetach = useCallback(
      async (uuids: string[]) => {
        const response = await detachLessons(uuids);

        if (response.error) {
          showToast(t('lesson.message.detach.error'), { type: 'error' });
        } else {
          showToast(t('lesson.message.detach.success'), { type: 'success' });
        }
      },
      [detachLessons],
    );

    const handleAddLesson = () => {
      setEditModalOpen(true);
    };

    const handleAddSubjectContainerLesson = () => {
      setEpochModalOpen(true);
    };

    const handleAddEpochClose = () => {
      setLessonUuid(null);
      setEpochModalOpen(false);
    };

    const handleClose = () => {
      setLessonUuid(null);
      setEditModalOpen(false);
    };

    const handleDelete = useCallback(
      async (lessons: LessonTableType[]) => {
        const usedInTimetableVersionData = await urqlClient
          .query<_UsedInVersionLessonsQuery, _UsedInVersionLessonsQueryVariables>(
            _UsedInVersionLessonsDocument,
            {
              uuids: lessons.map((l) => l.uuid),
            },
            { requestPolicy: 'network-only' },
          )
          .toPromise();

        const usedInTimetableVersionUuids = usedInTimetableVersionData.data?.lessons.map((l) => l.uuid) ?? [];
        const [canBeDeleted, canNotDelete] = partition(lessons, (l) => !usedInTimetableVersionUuids.includes(l.uuid));

        const message = (
          <Grid>
            <GridRow headline={t('lesson.confirm.delete.default', { count: canBeDeleted.length })}>
              <GridColumn width={12}>
                {canBeDeleted.map((l) => {
                  return (
                    <GridRow key={l.uuid} spacingTop={'s'} spacingBottom={'s'}>
                      <GridColumn width={4}>{l.subject}</GridColumn>
                      <GridColumn width={4}>{l.groupClassNames}</GridColumn>
                      <GridColumn width={4}>{l.teachers.map((t) => t.name).join(', ')}</GridColumn>
                    </GridRow>
                  );
                })}
              </GridColumn>
            </GridRow>
            {canNotDelete.length > 0 && (
              <GridRow headline={t('lesson.confirm.delete.withIgnored', { count: canNotDelete.length })}>
                <GridColumn width={12}>
                  {canNotDelete.map((l) => {
                    return (
                      <GridRow key={l.uuid} spacingTop={'s'} spacingBottom={'s'}>
                        <GridColumn width={4}>{l.subject}</GridColumn>
                        <GridColumn width={4}>{l.groupClassNames}</GridColumn>
                        <GridColumn width={4}>{l.teachers.map((t) => t.name).join(', ')}</GridColumn>
                      </GridRow>
                    );
                  })}
                </GridColumn>
              </GridRow>
            )}
          </Grid>
        );

        const confirmed = await confirm({ message: message });
        if (confirmed) {
          const response = await deleteLessons(canBeDeleted.map((l) => l.uuid));
          if (response.error) {
            showToast(t('lesson.message.delete.error'), { type: 'error' });
          } else {
            showToast(t('lesson.message.delete.success'), { type: 'success' });
          }
        }
      },
      [confirm, deleteLessons, t],
    );

    const handleDuplicate = useCallback(
      async (uuids: string[]) => {
        if (loadingState.some(({ uuid, loading }) => loading && uuids.includes(uuid))) {
          return;
        }
        const response = await duplicateLesson(uuids);
        if (response) {
          if (response.some((r) => r.error)) {
            showToast(t('lesson.message.duplicate.error'), { type: 'error' });
          } else {
            showToast(t('lesson.message.duplicate.success'), { type: 'success' });
          }
        } else {
          showToast(t('lesson.message.duplicate.error'), { type: 'error' });
        }
      },
      [duplicateLesson, loadingState, t],
    );

    const [, inheritLessonTeachers] = useInheritLessonTeachersMutation();

    const handleInheritLessonTeachers = useCallback(
      async (lessonUuids: string[], type: InheritLessonTeachersType) => {
        setLoading(true);
        const res = await inheritLessonTeachers(
          {
            lessonUuids,
            type,
          },
          context,
        );
        setLoading(false);

        if (res.error) {
          showUserErrorToast({ error: res.error });
        } else {
          showSuccessUpdateToast();
        }
      },
      [inheritLessonTeachers, context],
    );

    const getChipBgColor = (editStatus: LessonTableType['editStatus']) => {
      switch (editStatus) {
        case 'blocked':
          return 'var(--color-error)';
        case 'placedCards':
        case 'inVersion':
          return 'var(--color-primary-lighter)';
        case 'unused':
        default:
          return 'var(--color-grey-lighter)';
      }
    };

    const getChipIconColor = (editStatus: LessonTableType['editStatus']) => {
      switch (editStatus) {
        case 'blocked':
          return 'var(--chip-active-color)';
        case 'placedCards':
        case 'inVersion':
        case 'used':
          return 'var(--chip-color)';
        default:
          return 'var(--chip-color)';
      }
    };

    const getTooltipText = (editStatus: LessonTableType['editStatus']) => {
      switch (editStatus) {
        case 'blocked':
          return t('lesson.blocked');
        case 'placedCards':
          return t('lesson.placedCards');
        case 'inVersion':
          return t('lesson.inVersion');
        default:
          return t('lesson.editable');
      }
    };

    const columns: TableColumns<LessonTableType>[] = [
      {
        header: t('lesson.table.subject'),
        id: 'subject',
        size: 200,
        accessorKey: 'subject',
      },
      {
        header: t('common.type'),
        id: 'type',
        isMultiline: true,
        enableSorting: true,
        enableGlobalFilter: false,
        cell: ({ row }) => {
          if (row.original.isSubjectContainerLesson) {
            return t('epochs.plan');
          }
          const cardInfos = row.original.cardsInfos;
          return (
            <>
              {cardInfos && cardInfos.defaultCards.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    margin: 'var(--spacing-2) 0',
                  }}
                >
                  {cardInfos.defaultCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.typeLabel}>
                        {card.typeLabelShort}
                      </div>
                    );
                  })}
                </div>
              )}
              {cardInfos && cardInfos.subjectContainerCards.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-4)',
                    margin: 'var(--spacing-2) 0',
                  }}
                >
                  {cardInfos.subjectContainerCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.typeLabel}>
                        {card.typeLabelShort}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        },
        accessorFn: ({ cardsInfos, uuid, isSubjectContainerLesson }) => {
          const sum = (cardsInfos?.defaultCards.length ?? 0) + (cardsInfos?.subjectContainerCards.length ?? 0);
          let sortValue = '';
          if (isSubjectContainerLesson) {
            sortValue += `0000_${sum}_`;
          } else {
            sortValue += `${sum}_`;
          }

          if (cardsInfos && cardsInfos.defaultCards.length > 0) {
            sortValue += cardsInfos.defaultCards
              .map((dc) => {
                return dc.map((c) => c.typeLabelShort).join(',');
              })
              .join(',');
          }
          if (cardsInfos && cardsInfos.subjectContainerCards.length > 0) {
            sortValue += cardsInfos.subjectContainerCards
              .map((dc) => {
                return dc.map((c) => c.typeLabelShort).join(',');
              })
              .join(',');
          }
          return sortValue;
        },
        size: 90,
      },
      {
        header: t('common.hours'),
        id: 'cards',
        size: 150,
        isMultiline: true,
        enableSorting: false,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        canOverflow: true,
        cell: ({ row }) => {
          const cardInfos = row.original.cardsInfos;
          return (
            <>
              {cardInfos && cardInfos.defaultCards.length > 0 && (
                <div className={style['card-position']}>
                  {cardInfos.defaultCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.cardLabel}>
                        <LessonCard
                          label={card.cardLabelShort}
                          lessonClasses={card.lesson}
                          count={card.counter}
                          teacherColors={card.colors}
                          duration={card.duration}
                          showHover={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              {cardInfos && cardInfos.subjectContainerCards.length > 0 && (
                <div className={style['card-position']}>
                  {cardInfos.subjectContainerCards.map((group) => {
                    const card = group[0];
                    return (
                      <div key={card.uuid} title={card.cardLabel}>
                        <LessonCard
                          label={card.cardLabelShort}
                          lessonClasses={card.lesson}
                          count={card.counter}
                          teacherColors={card.colors}
                          duration={card.duration}
                          showHover={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        },
      },
      {
        header: t('lesson.table.classesGroups'),
        id: 'groupClassNames',
        accessorKey: 'groupClassNames',
        size: 125,
        cell: ({ row }) => {
          return <Tooltip content={row.original.groupClassNames}>{row.original.groupClassNames}</Tooltip>;
        },
      },
      {
        header: t('lesson.table.classes'),
        id: 'classes',
        size: 125,
        cell: ({ row }) => {
          const shortNames = row.original.classes.map((node) => node.name).join(', ');
          return <Tooltip content={shortNames}>{shortNames}</Tooltip>;
        },
        accessorFn: (data) => {
          const shortNames = data.classes.map((node) => node.name);
          return shortNames.join(', ');
        },
      },
      {
        header: t('lesson.table.groups'),
        id: 'groupNames',
        size: 125,
        cell: ({ row }) => {
          const groups = row.original.classes
            .flatMap((node) => node.groups)
            .map((group) => group?.shortName ?? '')
            .filter(onlyUnique)
            .join(', ');
          return <Tooltip content={groups}>{groups}</Tooltip>;
        },
        accessorFn: (data) => {
          const groups = data.classes.flatMap((node) => node.groups);
          return groups.map((group) => group?.shortName ?? '').join(', ');
        },
      },
      {
        header: t('lesson.table.teacher'),
        id: 'teachers',
        cell: ({ row }) => {
          const displayNamesShort = row.original.teachers.map((teacher) => teacher.name).join(', ');
          return <Tooltip content={displayNamesShort}>{displayNamesShort}</Tooltip>;
        },
        accessorFn: (originalRow) => {
          const displayNamesShort = originalRow.teachers.map((teacher) => teacher.name);
          return displayNamesShort.join(', ');
        },
        size: 125,
      },
      {
        header: t('deputate.title', { count: 1 }),
        id: 'deputate.titleSingluar',
        cell: ({ row }) => {
          const lessonInfo = row.original.teachingLoadInfo;
          const relevantDeputate = teacherUuid
            ? lessonInfo?.deputatByTeacher.get(teacherUuid)
            : classUuid
              ? lessonInfo?.deputatByClass.get(classUuid)
              : (lessonInfo?.deputat ?? 0);
          return (relevantDeputate ?? 0).toFixed(2);
        },
        accessorFn: (originalRow) => {
          const lessonInfo = originalRow.teachingLoadInfo;
          const relevantDeputate = teacherUuid
            ? lessonInfo?.deputatByTeacher.get(teacherUuid)
            : classUuid
              ? lessonInfo?.deputatByClass.get(classUuid)
              : (lessonInfo?.deputat ?? 0);
          return (relevantDeputate ?? 0).toFixed(2);
        },
        size: 50,
      },
      {
        header: t('lesson.basics.isElective'),
        id: 'elective',
        accessorKey: 'elective',
        type: 'boolean',
      },
      {
        header: t('lesson.basics.isTeachingLoadEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTeachingLoadEnabled.full'),
          tooltip: t('lesson.basics.isTeachingLoadEnabled.full'),
        },
        id: 'teachingLoadEnabled',
        accessorKey: 'teachingLoadEnabled',
        type: 'boolean',
      },
      {
        header: t('lesson.basics.isTimetableEnabled.short'),
        meta: {
          filterName: t('lesson.basics.isTimetableEnabled.full'),
          tooltip: t('lesson.basics.isTimetableEnabled.full'),
        },
        id: 'timetableEnabled',
        accessorKey: 'timetableEnabled',
        type: 'boolean',
      },
      {
        header: t('common.editable.short'),
        id: 'editStatus',
        meta: {
          filterName: t('common.editable.full'),
          tooltip: t('common.editable.full'),
        },
        cell: ({ row }) => {
          const currentRowData = queryLessonsData?.lessons.find((lesson) => lesson.uuid === row.original.uuid);

          const status = row.original.editStatus;
          const statusChip = (
            <Chip
              value={
                row.original.editStatus === 'blocked' ? (
                  <LockIcon color={getChipIconColor(status)} className={'smallest'} />
                ) : (
                  <UnlockIcon color={getChipIconColor(status)} className={'smallest'} />
                )
              }
              bgColor={getChipBgColor(status)}
            />
          );
          return (
            <>
              {status === 'unused' || !currentRowData ? (
                <>{statusChip}</>
              ) : (
                <Tooltip
                  content={<UsedInVersionsTooltipContent data={currentRowData} usedInText={getTooltipText(status)} />}
                >
                  {statusChip}
                </Tooltip>
              )}
            </>
          );
        },
        size: 48,
      },
    ];

    const dropdownItems = useCallback(
      (row: Row<LessonTableType>): DropdownMenuItem[] => {
        const qualified = queryLessonsData?.people.filter(
          (person) =>
            person.qualifications.some((q) => q.subject.uuid === row.original.subjectUuid) &&
            !row.original.teacherUuids.includes(person.uuid),
        );
        const teachers = qualified?.length
          ? qualified
          : queryLessonsData?.people.filter((person) => !row.original.teacherUuids.includes(person.uuid));

        if (lessonTabFilterType === 'subjectContainerLessons') {
          return [
            {
              label: t('epochs.editPlan'),
              onClick: () => {
                setLessonUuid(row.original.uuid);
                setEpochModalOpen(true);
              },
            },
            {
              type: 'ruler',
            },
            {
              label: t('common.delete'),
              type: 'default',
              color: 'error',
              disabled: row.original.editStatus === 'placedCards' || row.original.editStatus === 'blocked',
              onClick: () => {
                handleDelete([row.original]);
              },
            },
          ];
        }

        const moreThanOneTeacherFromCurriculum =
          (queryLessonsData?.lessons.find((lesson) => lesson.uuid === row.original.uuid)?.curriculum?.personsConnection
            .totalCount ?? 0) > 1;

        return [
          {
            label: t('lesson.edit'),
            onClick: () => {
              setLessonUuid(row.original.uuid);
              setEditModalOpen(true);
            },
          },
          {
            label: t('common.duplicate'),
            onClick: async () => {
              await handleDuplicate([row.original.uuid]);
            },
          },
          {
            label: t('lesson.actions.assignTo'),
            disabled: moreThanOneTeacherFromCurriculum || (row.original.teachers?.length ?? 0) > 1,
            subContent: teachers
              ? teachers.map((teacher) => {
                  return {
                    type: 'default',
                    label: teacher.selectName ?? '',
                    onClick: () => {
                      handleAssign(row.original, [teacher]);
                    },
                  };
                })
              : [],
          },
          {
            label: t('lesson.actions.detach'),
            onClick: () => {
              handleDetach([row.original.uuid]);
            },
          },
          {
            type: 'ruler',
          },
          {
            label: t('lesson.actions.toggle.isElective'),
            type: 'switch',
            value: row.original.elective ?? false,
            onValueChange: async () => {
              await updateCheckboxes({ type: 'elective', uuids: [row.original.uuid], value: !row.original.elective });
            },
          },
          {
            label: t('lesson.basics.isTeachingLoadEnabled.full'),
            type: 'switch',
            value: row.original.teachingLoadEnabled ?? false,
            onValueChange: async () => {
              await updateCheckboxes({
                type: 'teachingLoadEnabled',
                uuids: [row.original.uuid],
                value: !row.original.teachingLoadEnabled,
              });
            },
          },
          {
            label: t('lesson.basics.isTimetableEnabled.full'),
            type: 'switch',
            value: row.original.timetableEnabled ?? false,
            disabled:
              row.original.editStatus === 'placedCards' ||
              row.original.editStatus === 'inVersion' ||
              row.original.editStatus === 'blocked',
            onValueChange: async () => {
              await updateCheckboxes({
                type: 'timetableEnabled',
                uuids: [row.original.uuid],
                value: !row.original.timetableEnabled,
              });
            },
          },
          {
            type: 'ruler',
          },
          {
            label: t('common.delete'),
            color: 'error',
            onClick: () => {
              handleDelete([row.original]);
            },
          },
        ];
      },
      [
        handleAssign,
        handleDelete,
        handleDetach,
        handleDuplicate,
        queryLessonsData?.lessons,
        queryLessonsData?.people,
        updateCheckboxes,
        lessonTabFilterType,
        t,
      ],
    );

    const bulkEdit = useCallback(
      (selectedFlatRows: Row<LessonTableType>[]) => {
        const isSubjectContainerLesson = selectedFlatRows.every((s) => s.original.isSubjectContainerLesson);
        const dropDownData: DropdownMenuItem[] =
          isSubjectContainerLesson && permissionChecker?.isOmniAdmin()
            ? [
                {
                  label: t('lesson.actions.mergeSubjectContainerLessons'),
                  disabled: !isSubjectContainerLesson,
                  onClick: () => {
                    setLessonsToMerge(selectedFlatRows.map(({ original }) => original));
                    setMergeSubjectContainerLessonsModalOpen(true);
                  },
                },
              ]
            : [];

        dropDownData.push(
          ...[
            {
              label: t('lesson.basics.isElective'),
              disabled: isSubjectContainerLesson,
              subContent: [
                {
                  label: t('common.activateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'elective',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: true,
                    });
                    clearSelection();
                  },
                },
                {
                  label: t('common.deactivateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'elective',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: false,
                    });
                    clearSelection();
                  },
                },
              ],
            },
            {
              label: t('lesson.basics.isTimetableEnabled.full'),
              subContent: [
                {
                  label: t('common.activateAll'),
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'timetableEnabled',
                      uuids: selectedFlatRows
                        .filter(
                          ({ original }) =>
                            original.editStatus !== 'inVersion' && original.editStatus !== 'placedCards',
                        )
                        .map(({ original }) => original.uuid),
                      value: true,
                    });
                    clearSelection();
                  },
                },
                {
                  label: t('common.deactivateAll'),
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'timetableEnabled',
                      uuids: selectedFlatRows
                        .filter(
                          ({ original }) =>
                            original.editStatus !== 'inVersion' && original.editStatus !== 'placedCards',
                        )
                        .map(({ original }) => original.uuid),
                      value: false,
                    });
                    clearSelection();
                  },
                },
              ],
            },
            {
              label: t('lesson.basics.isTeachingLoadEnabled.full'),
              disabled: isSubjectContainerLesson,
              subContent: [
                {
                  label: t('common.activateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'teachingLoadEnabled',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: true,
                    });
                    clearSelection();
                  },
                },
                {
                  label: t('common.deactivateAll'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await updateCheckboxes({
                      type: 'teachingLoadEnabled',
                      uuids: selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                      value: false,
                    });
                    clearSelection();
                  },
                },
              ],
            },
          ],
        );

        const lessonPersonsSubmenuData: DropdownMenuItem[] =
          lessonTabFilterType === 'unassigned'
            ? []
            : [
                {
                  label: t('lesson.actions.detach'),
                  disabled: isSubjectContainerLesson,
                  onClick: async () => {
                    await handleDetach(
                      selectedFlatRows
                        .filter(({ original }) => !original.isSubjectContainerLesson)
                        .map(({ original }) => original.uuid),
                    );
                    clearSelection();
                  },
                },
              ];

        if (lessonTabFilterType === 'unassigned') {
          lessonPersonsSubmenuData.push(
            {
              label: t('lesson.actions.sameStudents'),
              disabled: isSubjectContainerLesson,
              onClick: async () => {
                await handleInheritLessonTeachers(
                  selectedFlatRows
                    .filter(({ original }) => !original.isSubjectContainerLesson)
                    .map(({ original }) => original.uuid),
                  InheritLessonTeachersType.SameStudents,
                );
                clearSelection();
              },
            },
            {
              label: t('lesson.actions.sameClassLevel'),
              disabled: isSubjectContainerLesson,
              onClick: async () => {
                await handleInheritLessonTeachers(
                  selectedFlatRows
                    .filter(({ original }) => !original.isSubjectContainerLesson)
                    .map(({ original }) => original.uuid),
                  InheritLessonTeachersType.SameClassLevel,
                );
                clearSelection();
              },
            },
          );
        }
        return (
          <DropdownMenu
            data={[
              ...dropDownData,
              { type: 'ruler' },
              {
                label: t('persons.title', { count: 2 }),
                disabled: isSubjectContainerLesson,
                subContent: lessonPersonsSubmenuData,
              },
              { type: 'ruler' },
              {
                label: t('common.delete'),
                color: 'error',
                onClick: async () => {
                  await handleDelete(selectedFlatRows.map(({ original }) => original));
                  clearSelection();
                },
              },
            ]}
          />
        );
      },
      [
        clearSelection,
        handleDelete,
        handleDetach,
        t,
        updateCheckboxes,
        handleInheritLessonTeachers,
        lessonTabFilterType,
        permissionChecker?.isOmniAdmin,
      ],
    );

    const lastCol = useCallback(
      (row: Row<LessonTableType>) => {
        if (row.original.editStatus === 'blocked') {
          return (
            <Button
              hierarchy={'secondary'}
              icon={<VisibleIcon className='small' />}
              onClick={() => {
                setLessonUuid(row.original.uuid);
                if (lessonTabFilterType === 'subjectContainerLessons') {
                  setEpochModalOpen(true);
                } else {
                  setEditModalOpen(true);
                }
              }}
            ></Button>
          );
        }

        return (
          <Dropdown
            trigger={
              <Button
                isLoading={loadingState.some(({ loading, uuid }) => uuid === row.original.uuid && loading)}
                hierarchy='secondary'
                icon={<DotsHorizontalIcon className='small' />}
              />
            }
          >
            <DropdownMenu maxHeight='s' data={dropdownItems(row)} />
          </Dropdown>
        );
      },
      [dropdownItems, loadingState, lessonTabFilterType],
    );

    const actionBarSettings = {
      showSelectedRowsCount: true,
      showExpertFilter: true,
      showPrintButton: true,
      showBulkEdit: true,
      showAddButton: false,
      extendedActionsRight:
        lessonTabFilterType === 'subjectContainerLessons' ? (
          <Button hierarchy='tertiary' icon={<AddIcon />} onClick={handleAddSubjectContainerLesson}>
            {t('common.add')}
          </Button>
        ) : (
          <Button hierarchy='tertiary' icon={<AddIcon />} onClick={handleAddLesson}>
            {t('common.add')}
          </Button>
        ),
    };

    const getLessonCards = (lesson: LessonTableType) => {
      const cardInfos = lesson.cardsInfos;
      return (
        <div className={style['card-position']}>
          {cardInfos?.defaultCards.map((group) => {
            const card = group[0];
            return (
              <div key={card.uuid} title={card.cardLabel}>
                <LessonCard
                  label={card.cardLabelShort}
                  lessonClasses={card.lesson}
                  count={card.counter}
                  teacherColors={card.colors}
                  duration={card.duration}
                  showHover={false}
                />
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <>
        <Table<LessonTableType>
          showBorderRadius
          showShadow
          canScroll
          minHeight={1000}
          showSelected
          showVisibility
          loading={loading || loadingState?.some(({ uuid, loading }) => uuid === 'all' && loading)}
          isOnWhite={false}
          printerSettings={{
            headline: pimAuthClaims.getProfile()?.organization.name,
            subline: `${t('lesson.title', { count: 2 })} - ${t('common.schoolYear')} ${schoolYear?.shortName}`,
            filename: `${t('lesson.title', { count: 2 })}_${schoolYear?.shortName}`,
          }}
          showActionBar
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          showSelect
          showSort
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={saveColumnVisibility}
          sorting={sorting}
          onSortingChange={saveSorting}
          bulkEditDropdownContent={bulkEdit}
          actionBarSettings={actionBarSettings}
          columns={columns}
          data={lessonsData}
          lastCol={lastCol}
          showPagination
          pageSize={50}
        />

        <DeputateLessonModal
          lessonUuid={lessonUuid}
          isOpen={editModalOpen}
          onClose={handleClose}
          defaultInitialValues={defaultInitialValues}
        />
        <DeputateEpochModal
          lessonUuid={lessonUuid}
          isOpen={epochModalOpen}
          onClose={handleAddEpochClose}
          classUuid={classUuid}
        />
        <MergeSubjectContainerLessonsModal
          open={mergeSubjectContainerLessonsModalOpen}
          onClose={() => {
            setMergeSubjectContainerLessonsModalOpen(false);
          }}
          handleMerge={async ({ cardsFromLesson, lessonUuids, newCards, removePlacedCards }) => {
            setLoading(true);
            await mergeLessons(
              {
                lessonUuids: lessonUuids,
                cardsFromLesson: cardsFromLesson,
                newCards: newCards ?? [],
                removePlacedCards: removePlacedCards ?? false,
              },
              context,
            );
            setLoading(false);
            setMergeSubjectContainerLessonsModalOpen(false);
            setLessonsToMerge([]);
          }}
          lessons={lessonsToMerge.map((lesson) => {
            return {
              uuid: lesson.uuid,
              name: `${lesson.subject} - ${lesson.groupClassNames}`,
              subjectContainerUuid: lesson.subjectUuid,
              subjectName: lesson.subject,
              onlyInTimetableVersion: false,
              cards: getLessonCards(lesson),
            };
          })}
        />
        <ConfirmationDialog />
      </>
    );
  },
);
